import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";

/**
 * `types.ts` is generated from the deployed schema, so RPCs that ship with this
 * change's migration aren't in it yet. Narrow escape hatch rather than the
 * `as any` alias used elsewhere, so this stays lint-clean.
 *
 * `.bind` is required, not decorative: SupabaseClient.rpc is `return
 * this.rest.rpc(...)`, so detaching the method leaves `this` undefined and it
 * throws "Cannot read properties of undefined (reading 'rest')" at call time.
 */
type UntypedRpc = (
  fn: string,
  args: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message: string } | null }>;

export interface RecycledPhotoRow {
  id: string;
  asset_id: string;
  document_name: string | null;
  file_path: string | null;
  deleted_at: string | null;
}

/**
 * A property's soft-deleted asset photos, via the recycle-bin RPC.
 *
 * An RPC rather than a select: every normal read of this table filters
 * `deleted_at IS NULL`, and the RPC is also where authorization is enforced —
 * RLS is disabled on asset_documents, so its policies are inert.
 */
export function useRecycledAssetPhotos(propertyId: string | null) {
  return useQuery<RecycledPhotoRow[]>({
    queryKey: ["recycled-asset-photos", propertyId],
    queryFn: async (): Promise<RecycledPhotoRow[]> => {
      if (!propertyId) return [];
      const rpc = supabase.rpc.bind(supabase) as unknown as UntypedRpc;
      const { data, error } = await rpc("list_recycled_asset_documents", {
        p_property_id: propertyId,
      });
      if (error) throw error;
      return ((data as RecycledPhotoRow[]) ?? []).map((r) => ({
        id: r.id,
        asset_id: r.asset_id,
        document_name: r.document_name,
        file_path: r.file_path,
        deleted_at: r.deleted_at,
      }));
    },
    enabled: !!propertyId,
    staleTime: 60 * 1000,
  });
}
